import type { Dispatch, SetStateAction } from 'react';
import { decimal, label, projectDate } from '@entities/ksp/format';
import { usePeopleNames } from '@entities/ksp/usePeopleNames';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import { Button } from '@shared/ui/Button';
import { Card, Field, Input, Row, Select, Small } from '../styles';
import { choice, textField } from '../types';
import { PagedTable } from '../components/CollectionTable';

export interface PeriodsTabProps {
  workspace: KspWorkspace;
  projectId: string;
  versionId?: string | null;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  workOptions: { value: string; label: string }[];
  dailyItems: Array<{
    activity_id: string;
    date: string;
    quantity: string;
    unit: string;
    responsible_id: string;
  }>;
  setDailyItems: Dispatch<
    SetStateAction<
      Array<{
        activity_id: string;
        date: string;
        quantity: string;
        unit: string;
        responsible_id: string;
      }>
    >
  >;
  periodStart: string;
  setPeriodStart: (val: string) => void;
  periodEnd: string;
  setPeriodEnd: (val: string) => void;
}

export function PeriodsTab({
  workspace: d,
  projectId,
  versionId,
  permission,
  open,
  execute,
  workOptions,
  dailyItems,
  setDailyItems,
  periodStart,
  setPeriodStart,
  periodEnd,
  setPeriodEnd,
}: PeriodsTabProps) {
  const timezone = d.settings?.timezone ?? 'Europe/Moscow';
  const person = usePeopleNames();
  const activity = (id: string) => d.activities.find((a) => a.id === id);
  // Конец периода хранится «не включая»: на экране — последний день периода.
  const lastDay = (exclusive: string) => {
    const t = Date.parse(`${exclusive}T00:00:00Z`);
    return Number.isFinite(t) ? new Date(t - 86400000).toISOString().slice(0, 10) : exclusive;
  };
  return (
    <>
      <Card>
        <h3>Месячно-суточное задание</h3>
        <Row>
          <Field>
            Начало периода
            <Input
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
          </Field>
          <Field>
            Конец периода (не включается)
            <Input
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </Field>
          <Button
            disabled={!permission() || !d.summary}
            onClick={() =>
              setDailyItems((prev) => [
                ...prev,
                {
                  activity_id: '',
                  date: periodStart,
                  quantity: '',
                  unit: '',
                  responsible_id: '',
                },
              ])
            }
          >
            Добавить суточную строку
          </Button>
        </Row>
        {dailyItems.map((item, i) => (
          <Row key={i}>
            <Field>
              Работа
              <Select
                value={item.activity_id}
                onChange={(e) =>
                  setDailyItems((items) =>
                    items.map((value, j) =>
                      j === i
                        ? {
                            ...value,
                            activity_id: e.target.value,
                            unit:
                              d.activities.find((a) => a.id === e.target.value)?.unit ?? '',
                          }
                        : value,
                    ),
                  )
                }
              >
                <option value="">Выберите</option>
                {workOptions.map((option) => (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            {(['date', 'quantity', 'unit', 'responsible_id'] as const).map((key) => (
              <Field key={key}>
                {
                  {
                    date: 'Дата',
                    quantity: 'Количество',
                    unit: 'Единица',
                    responsible_id: 'Ответственный',
                  }[key]
                }
                <Input
                  type={key === 'date' ? 'date' : 'text'}
                  value={item[key]}
                  onChange={(e) =>
                    setDailyItems((items) =>
                      items.map((value, j) =>
                        j === i ? { ...value, [key]: e.target.value } : value,
                      ),
                    )
                  }
                />
              </Field>
            ))}
            <Button
              $variant="ghost"
              onClick={() => setDailyItems((items) => items.filter((_, j) => i !== j))}
            >
              Убрать
            </Button>
          </Row>
        ))}
        <Button
          disabled={
            !permission() || !d.summary || !dailyItems.length || !periodStart || !periodEnd
          }
          onClick={() =>
            open(
              'Сохранить задание',
              [
                textField('basis', 'Основание'),
                choice(
                  'quantity_precision',
                  'Точность суточных объёмов',
                  [
                    { value: '2', label: '0.01' },
                    { value: '3', label: '0.001' },
                    { value: '8', label: '0.00000001' },
                  ],
                  '8',
                ),
              ],
              (values) =>
                execute(`projects/${projectId}/period-plans`, {
                  ...values,
                  quantity_precision: Number(values.quantity_precision),
                  version_id: versionId,
                  snapshot_id: d.summary?.snapshot_id,
                  start_date: periodStart,
                  end_date_exclusive: periodEnd,
                  items: dailyItems.map((item) => ({
                    ...item,
                    responsible_id: item.responsible_id || null,
                  })),
                }),
            )
          }
        >
          Сохранить проект задания
        </Button>
        <Small>
          Количество сохраняется точно. Суммы и допустимые рабочие окна проверяет сервер;
          выпущенное задание меняется новой ревизией.
        </Small>
      </Card>
      {d.period_plans.map((plan) => (
        <Card key={plan.id}>
          <Row>
            <h3>
              {projectDate(plan.period_start, timezone)} — {projectDate(lastDay(plan.period_finish), timezone)}
              {' · '}ред. {plan.revision}
            </h3>
            <strong>{label(plan.status)}</strong>
            <Button
              disabled={!permission('period.release') || plan.status === 'released'}
              onClick={() =>
                open('Выпустить задание', [textField('basis', 'Основание')], (values) =>
                  execute(`period-plans/${plan.id}/release`, values),
                )
              }
            >
              Выпустить
            </Button>
          </Row>
          <PagedTable
            rows={plan.items}
            columns={[
              {
                name: 'Работа',
                value: (item) => {
                  const work = activity(item.activity_id);
                  return work ? (
                    <span title={work.name}>
                      {work.code} · {work.name}
                    </span>
                  ) : (
                    'работа не найдена в версии'
                  );
                },
              },
              { name: 'Дата', value: (item) => projectDate(item.date, timezone) },
              { name: 'Объём', value: (item) => `${decimal(item.quantity)} ${item.unit}` },
              {
                name: 'Ответственный',
                value: (item) => {
                  const who = person(item.responsible_id);
                  return <span title={who.title}>{who.text}</span>;
                },
              },
              {
                name: 'Суточное распределение',
                value: (item) =>
                  item.days?.map((day) => (
                    <Small key={day.date}>
                      {projectDate(day.date, timezone)}: {decimal(day.quantity)} {item.unit}
                    </Small>
                  )),
              },
            ]}
          />
        </Card>
      ))}
    </>
  );
}
